import React from 'react';

import { format } from 'date-fns';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import FormHorizontalSeparator from 'CommonComponents/FormHorizontalSeparator';
import FormSectionHeading from 'CommonComponents/FormSectionHeading';
import FormTextRow from 'CommonComponents/FormTextRow';

import FileDownloadIcon from 'Assets/icons/file-download.svg';

const DATE_FIELDS = [
    {
        label: $t('Scope Issue to Market'),
        items: ['target_scope_issue_to_market_date', 'expected_scope_issue_to_market_date', 'actual_scope_issue_to_market_date'],
    },
    { label: $t('Response Due Date'), items: ['target_response_due_date', 'expected_response_due_date', 'actual_response_due_date'] },
    { label: $t('Decision Date'), items: ['target_decision_date', 'expected_decision_date', 'actual_decision_date'] },
    { label: $t('Contract Award Date'), items: ['target_contract_award_date', 'expected_contract_award_date', 'actual_contract_award_date'] },
    {
        label: $t('Contract Completion Date'),
        items: ['target_contract_completion_date', 'expected_contract_completion_date', 'actual_contract_completion_date'],
    },
];

export default function ProcurementModalContent(props) {
    const downloadAttachment = (e, attachment) => {
        e.preventDefault();
        const url = props.downloadAttachmentUrl.replace('ITEM_ID', props.procurement?.id).replace('ATTACHMENT_ID', attachment.id);

        axios({
            url: url,
            method: 'GET',
            responseType: 'blob',
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', attachment.file_name);
            document.body.appendChild(link);
            link.click();
        });
    };

    return (
        <div className="modal__form-container">
            <FormSectionHeading heading={$t('Summary')}></FormSectionHeading>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <div className="tw-flex tw-flex-col tw-space-y-4">
                    <FormTextRow label={$t('Name')} text={props.procurement.name}></FormTextRow>
                    <FormTextRow label={$t('Description')} text={props.procurement.description}></FormTextRow>
                    <FormTextRow label={$t('Value')} text={formatToFixedMoneyWithLocale(props.procurement.value)}></FormTextRow>
                    <FormTextRow label={$t('Procurement Category')} text={props.procurement.procurement_category?.name}></FormTextRow>
                    <FormTextRow label={$t('Additional Procurement Category')} text={props.procurement.additional_category}></FormTextRow>
                    <FormTextRow label={$t('PR Number')} text={props.procurement.pr_number}></FormTextRow>
                </div>
                <div className="tw-flex tw-flex-col tw-space-y-4">
                    <FormSectionHeading heading={$t('Supplier Information')} />
                    <FormTextRow label={$t('Owner')} text={props.procurement.owner?.first_name + ' ' + props.procurement.owner?.last_name}></FormTextRow>
                    <FormTextRow label={$t('Status')} text={props.procurement.procurement_status?.title}></FormTextRow>
                    <FormTextRow label={$t('Policy')} text={props.procurement.procurement_status?.method}></FormTextRow>
                    <FormTextRow label={$t('Priority')} text={props.procurement.priority_label}></FormTextRow>
                    <FormTextRow label={$t('Complexity')} text={props.procurement.complexity_label}></FormTextRow>
                    <FormTextRow label={$t('EA Number')} text={props.procurement.po_number}></FormTextRow>
                </div>
            </div>

            <FormHorizontalSeparator />

            <KeyDatesGrid procurement={props.procurement} />

            <FormHorizontalSeparator />

            <FormSectionHeading heading={$t('Possible Tenderers')}></FormSectionHeading>
            <TenderersGrid tenderers={props.procurement.tenderers} />

            <FormHorizontalSeparator />

            <FormSectionHeading heading={$t('Evaluators')}></FormSectionHeading>
            <EvaluatorsGrid evaluators={props.procurement.evaluators} />

            <FormHorizontalSeparator />

            <FormSectionHeading heading={$t('Attachments')}></FormSectionHeading>
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                {props.procurement.attachments?.map((attachment) => (
                    <div key={attachment.id} className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                        <a href="#" onClick={(e) => downloadAttachment(e, attachment)}>
                            <FileDownloadIcon className="tw-w-2.5 tw-h-2.5 tw-mr-2" /> {attachment.file_name}
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
}

function KeyDatesGrid({ procurement }) {
    const getLabelText = (index) => {
        if (index == 0) {
            return 'Target';
        } else if (index == 1) {
            return 'Expected';
        } else {
            return 'Actual';
        }
    };

    return (
        <>
            <div className="tw-hidden md:tw-grid tw-grid-cols-12 tw-gap-4">
                <div className="tw-col-span-12 md:tw-col-span-3">
                    <FormSectionHeading heading={$t('Key Dates')}></FormSectionHeading>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-3 tw-text-center">
                    <FormSectionHeading heading={$t('Target')}></FormSectionHeading>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-3 tw-text-center">
                    <FormSectionHeading heading={$t('Expected')}></FormSectionHeading>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-3 tw-text-center">
                    <FormSectionHeading heading={$t('Actual')}></FormSectionHeading>
                </div>
            </div>
            {DATE_FIELDS.map(({ label, items }, index) => (
                <div className="tw-grid tw-grid-cols-12 tw-gap-4" key={label + index}>
                    <div className="tw-col-span-12 md:tw-col-span-3">
                        <label className="tw-text-sm tw-font-semibold tw-block md:tw-hidden">{label}</label>
                        <label className="text-input__label tw-hidden md:tw-block">{label}</label>
                    </div>
                    {items.map((item, index) => (
                        <div className="tw-col-span-12 md:tw-col-span-3" key={item}>
                            <div className="tw-grid tw-grid-cols-12 tw-gap-4 md:tw-gap-0">
                                <label className="tw-col-span-3 md:tw-col-span-12 text-input__label tw-block md:tw-hidden">{$t(getLabelText(index))}</label>

                                <div className="tw-col-span-9 md:tw-col-span-12">
                                    <p className="modal__text tw-text-center" key={item}>
                                        {procurement[item] ? format(new Date(procurement[item]), 'yyyy-MM-dd') : '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </>
    );
}

function TenderersGrid({ tenderers }) {
    if (!tenderers?.length) {
        return <div className="modal__text">{$t('This procurement has no tenderers')}</div>;
    }

    return (
        <>
            <div className="tw-grid tw-grid-cols-12 tw-gap-4">
                <div className="tw-col-span-12 md:tw-col-span-3">
                    <FormSectionHeading heading={$t('Company Name')}></FormSectionHeading>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-3">
                    <FormSectionHeading heading={$t('Website')}></FormSectionHeading>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-3">
                    <FormSectionHeading heading={$t('Contact Name')}></FormSectionHeading>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-3">
                    <FormSectionHeading heading={$t('Contact Email')}></FormSectionHeading>
                </div>
            </div>

            {tenderers.map((tenderer) => {
                return (
                    <div className="tw-grid tw-grid-cols-12 tw-gap-4" key={tenderer.id}>
                        <div className="tw-col-span-3">
                            <p className="modal__text">{tenderer.company_name}</p>
                        </div>
                        <div className="tw-col-span-3">
                            <p className="modal__text">{tenderer.website}</p>
                        </div>
                        <div className="tw-col-span-3">
                            <p className="modal__text">{tenderer.contact_name}</p>
                        </div>
                        <div className="tw-col-span-3">
                            <p className="modal__text">{tenderer.contact_email}</p>
                        </div>
                    </div>
                );
            })}
        </>
    );
}

function EvaluatorsGrid({ evaluators }) {
    if (!evaluators?.length) {
        return <div className="modal__text">{$t('This procurement has no evaluators')}</div>;
    }

    return (
        <>
            <div className="tw-grid tw-grid-cols-12 tw-gap-4">
                <div className="tw-col-span-12 md:tw-col-span-6">
                    <FormSectionHeading heading={$t('Name')}></FormSectionHeading>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-6">
                    <FormSectionHeading heading={$t('Email')}></FormSectionHeading>
                </div>
            </div>

            {evaluators.map((evaluator) => {
                return (
                    <div className="tw-grid tw-grid-cols-12 tw-gap-4" key={evaluator.id}>
                        <div className="tw-col-span-6">
                            <p className="modal__text">
                                {evaluator.first_name} {evaluator.last_name}
                            </p>
                        </div>
                        <div className="tw-col-span-6">
                            <p className="modal__text">{evaluator.email}</p>
                        </div>
                    </div>
                );
            })}
        </>
    );
}
